import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Small components (P0-9 deliverable 10): Button, IconButton, Kbd, Pill,
 * StateBadge, DateChip, SrdBadge, EmptyState, Banner, Toast (with UI r4's
 * countdown) and the form controls. Styled only from tokens (packages/ui/src/styles/).
 * Inputs, checkboxes, radios and switches draw their resting boundary in
 * `controlBorder`.
 */
import { Button as BaseButton } from '@base-ui/react/button';
import { useEffect, useId, useRef, useState } from 'react';
import { TypeGlyph } from '../glyphs/type-glyph.js';
import { Icon } from '../icon/icon.js';
import { createCountdown, secondsLeft } from './countdown.js';
import { cx } from './infra.js';
/** The keys that activate a button: inert while it is disabled but focusable. */
const ACTIVATION_KEYS = new Set(['Enter', ' ']);
const stop = (event) => event.preventDefault();
/**
 * Props for a button that is disabled but keeps the focus (`focusableWhenDisabled`). Base UI's own
 * handling prevents the default of every key but Tab, so the app's keymaps (Run mode's J, K and R)
 * read the press as handled and drop it (UI r5b, the routed item of DECISIONS 764). Here the button
 * stays enabled for Base UI and is marked `aria-disabled`; a click, a pointer press, Enter and Space
 * stay inert (as Base UI's), and every other key (Tab, the arrows, letters) bubbles untouched. The consumer's own
 * handlers are not called, as for a disabled button.
 */
export function inertButtonProps(rest) {
    const { disabled, focusableWhenDisabled, onClick: _click, onKeyDown: _down, onKeyUp: _up, onPointerDown: _pointer, onMouseDown: _mouse, ...others } = rest;
    if (!(disabled && focusableWhenDisabled))
        return rest;
    return {
        ...others,
        'aria-disabled': true,
        'data-disabled': '',
        onClick: stop,
        onPointerDown: stop,
        onKeyDown: (event) => {
            if (ACTIVATION_KEYS.has(event.key))
                event.preventDefault();
        },
        onKeyUp: (event) => {
            if (event.key === ' ')
                event.preventDefault();
        },
    };
}
export function Button({ variant = 'secondary', size = 'md', icon, iconEnd, busy, className, children, type = 'button', ...rest }) {
    return (_jsxs(BaseButton, { ...inertButtonProps(rest), type: type, "aria-busy": busy || undefined, className: cx('cx-button', `cx-button--${variant}`, `cx-button--${size}`, className), children: [busy ? (_jsx("span", { className: "cx-spinner", "aria-hidden": "true" })) : (icon && _jsx(Icon, { name: icon, weight: rest['aria-pressed'] === true ? 'fill' : 'regular' })), children !== undefined && _jsx("span", { className: "cx-button__label", children: children }), iconEnd && _jsx(Icon, { name: iconEnd })] }));
}
export function IconButton({ icon, label, pressed, variant = 'ghost', className, ...rest }) {
    return (_jsx(BaseButton, { ...inertButtonProps(rest), type: rest.type ?? 'button', "aria-label": label, title: label, "aria-pressed": pressed, className: cx('cx-icon-button', `cx-button--${variant}`, className), children: _jsx(Icon, { name: icon, weight: pressed ? 'fill' : 'regular' }) }));
}
/** A keyboard key or shortcut, set in the mono face. */
export function Kbd({ children, label }) {
    return (_jsx("kbd", { className: "cx-kbd", ...(label ? { 'aria-label': label } : {}), children: children }));
}
export function Pill({ icon, tone = 'neutral', children, className, }) {
    return (_jsxs("span", { className: cx('cx-pill', className), "data-tone": tone, children: [icon, _jsx("span", { className: "cx-pill__label", children: children })] }));
}
/** Secret states (baseline section 4; test strategy section 3). */
export const SECRET_STATES = {
    unknown: { icon: 'eye-off', label: 'Unknown', tone: 'neutral' },
    partial: { icon: 'circle-half', label: 'Partial', tone: 'warning' },
    tentative: { icon: 'seal-question', label: 'Tentative', tone: 'ai' },
    known: { icon: 'eye', label: 'Known', tone: 'success' },
    orphaned: { icon: 'link-broken', label: 'Orphaned', tone: 'danger' },
    nested: { icon: 'stack', label: 'Nested', tone: 'neutral' },
};
export const CANON_STATES = {
    draft: { icon: 'pencil', label: 'Draft', tone: 'neutral' },
    canon: { icon: 'seal-check', label: 'Canon', tone: 'success' },
    retired: { icon: 'x-circle', label: 'Retired', tone: 'neutral' },
};
export const PLAY_STATES = {
    unplayed: { icon: 'circle', label: 'Unplayed', tone: 'neutral' },
    played: { icon: 'check-circle', label: 'Played', tone: 'success' },
    skipped: { icon: 'skip', label: 'Skipped', tone: 'neutral' },
    diverged: { icon: 'branch', label: 'Diverged', tone: 'warning' },
};
/** State is never color alone: an icon and text, always. */
export function StateBadge({ state, label, detail, className, }) {
    return (_jsxs("span", { className: cx('cx-state', className), "data-tone": state.tone, "data-state-badge": "", children: [_jsx(Icon, { name: state.icon, size: 14 }), _jsx("span", { className: "cx-state__label", children: label ?? state.label }), detail && _jsx("span", { className: "cx-state__detail", children: detail })] }));
}
/** The SRD badge on reference entries (amendments 2.1 section 7, fix 7). */
export function SrdBadge() {
    return (_jsx("abbr", { className: "cx-srd-badge", title: "System Reference Document 5.1 (read-only)", children: "SRD" }));
}
export function DateChip({ label, precision = 'exact', onOpen, className }) {
    return (_jsxs("button", { type: "button", className: cx('cx-date-chip', className), "data-precision": precision, "aria-label": `Date: ${label}${precision === 'fuzzy' ? ' (approximate)' : ''}`, onClick: onOpen, children: [_jsx(Icon, { name: precision === 'fuzzy' ? 'hourglass' : 'calendar', size: 14 }), _jsx("span", { className: "cx-date-chip__label", children: label })] }));
}
/* ------------------------------------------------------------------ */
/* loading, empty, error, messages                                     */
/* ------------------------------------------------------------------ */
export function EmptyState({ icon = 'info', glyph, title, children, action, headingLevel = 3, }) {
    const H = `h${headingLevel}`;
    return (_jsxs("div", { className: "cx-empty", children: [_jsx("span", { className: "cx-empty__icon", children: glyph ? _jsx(TypeGlyph, { type: glyph, size: 28 }) : _jsx(Icon, { name: icon, size: 28 }) }), _jsx(H, { className: "cx-empty__title", children: title }), children && _jsx("div", { className: "cx-empty__body", children: children }), action && _jsx("div", { className: "cx-empty__action", children: action })] }));
}
export function Banner({ tone = 'info', title, children, action, live = 'polite', }) {
    const icon = tone === 'danger'
        ? 'warning-circle'
        : tone === 'warning'
            ? 'warning'
            : tone === 'success'
                ? 'check-circle'
                : 'info';
    return (_jsxs("div", { className: "cx-banner", "data-tone": tone, role: live === 'off' ? undefined : live === 'assertive' ? 'alert' : 'status', children: [_jsx(Icon, { name: icon, size: 18 }), _jsxs("div", { className: "cx-banner__text", children: [title && _jsx("strong", { className: "cx-banner__title", children: title }), _jsx("span", { children: children })] }), action && _jsx("div", { className: "cx-banner__action", children: action })] }));
}
/** A notice at the edge of the screen. It never takes focus and is not a layer. */
export function Toast(props) {
    if (props.duration === undefined)
        return _jsx(PlainToast, { ...props });
    return _jsx(TimedToast, { ...props, duration: props.duration });
}
function PlainToast({ children, action, onDismiss, testId, testIds }) {
    return (_jsxs("div", { className: "cx-toast", role: "status", "data-testid": testId, children: [_jsx("span", { className: "cx-toast__text", children: children }), action && (_jsx(Button, { size: "sm", variant: "ghost", busy: action.busy === true, "data-testid": testIds?.action, onClick: () => {
                    if (!action.busy)
                        action.onClick();
                }, children: action.label })), onDismiss && (_jsx(IconButton, { icon: "x", label: "Dismiss", size: "sm", "data-testid": testIds?.dismiss, onClick: onDismiss }))] }));
}
/** How often a running toast redraws its seconds and bar. */
const TOAST_TICK_MS = 250;
function TimedToast({ children, action, onDismiss, duration, onExpire, paused = false, elapsed = 0, testId, testIds, }) {
    const descId = useId();
    const [countdown] = useState(() => createCountdown(duration, elapsed));
    const [now, setNow] = useState(() => Date.now());
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    // The action was pressed and not yet handed back; Dismiss was pressed.
    const [acted, setActed] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const expired = useRef(false);
    const expire = useRef(onExpire);
    expire.current = onExpire;
    const busy = action?.busy === true;
    const wasBusy = useRef(busy);
    useEffect(() => {
        if (wasBusy.current && !busy)
            setActed(false);
        wasBusy.current = busy;
    }, [busy]);
    const hold = paused || hovered || focused || busy || acted || dismissed;
    useEffect(() => {
        const start = Date.now();
        if (hold || expired.current) {
            countdown.pause(start);
            setNow(start);
            return;
        }
        countdown.resume(start);
        setNow(start);
        const tick = setInterval(() => setNow(Date.now()), TOAST_TICK_MS);
        const end = setTimeout(() => {
            clearInterval(tick);
            setNow(Date.now());
            if (expired.current)
                return;
            expired.current = true;
            expire.current?.();
        }, countdown.remaining(start));
        return () => {
            clearInterval(tick);
            clearTimeout(end);
            countdown.pause(Date.now());
        };
    }, [hold, countdown]);
    const remaining = countdown.remaining(now);
    const seconds = secondsLeft(duration);
    return (_jsxs("div", { className: "cx-toast cx-toast--timed", role: "status", "data-testid": testId, "data-paused": hold ? '' : undefined, onPointerEnter: () => setHovered(true), onPointerLeave: () => setHovered(false), onFocus: () => setFocused(true), onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setFocused(false);
        }, children: [_jsx("span", { className: "cx-toast__text", children: children }), _jsx("span", { id: descId, className: "cx-visually-hidden", children: action
                    ? `${action.label} is available for ${seconds} seconds.`
                    : `This closes after ${seconds} seconds.` }), _jsxs("span", { className: "cx-toast__time", "aria-hidden": "true", "data-relative-time": "", children: [secondsLeft(remaining), " s"] }), action && (_jsx(Button, { size: "sm", variant: "ghost", busy: busy, "aria-describedby": descId, "data-testid": testIds?.action, onClick: () => {
                    if (busy || expired.current)
                        return;
                    setActed(true);
                    action.onClick();
                }, children: action.label })), onDismiss && (_jsx(IconButton, { icon: "x", label: "Dismiss", size: "sm", "data-testid": testIds?.dismiss, onClick: () => {
                    setDismissed(true);
                    onDismiss();
                } })), _jsx("span", { className: "cx-toast__bar", "aria-hidden": "true", children: _jsx("span", { className: "cx-toast__fill", style: { ['--cx-toast-left']: duration > 0 ? remaining / duration : 0 } }) })] }));
}
export function TextField({ label, description, hideLabel, leading, className, ...rest }) {
    const id = useId();
    const descId = description ? `${id}-desc` : undefined;
    return (_jsxs("div", { className: cx('cx-field', className), children: [_jsx("label", { htmlFor: id, className: cx('cx-field__label', hideLabel && 'cx-visually-hidden'), children: label }), _jsxs("span", { className: "cx-input", "data-control-boundary": "", children: [leading, _jsx("input", { id: id, "aria-describedby": descId, className: "cx-input__el", ...rest })] }), description && (_jsx("span", { id: descId, className: "cx-field__description", children: description }))] }));
}
export function Checkbox({ label, checked, onChange, description, disabled, }) {
    return (_jsxs("label", { className: cx('cx-checkbox', disabled && 'cx-checkbox--disabled'), children: [_jsx("input", { type: "checkbox", className: "cx-checkbox__input", checked: checked, disabled: disabled, onChange: (e) => onChange(e.currentTarget.checked) }), _jsx("span", { className: "cx-checkbox__box", "data-control-boundary": "", "aria-hidden": "true", children: _jsx(Icon, { name: "check", size: 12, weight: "fill" }) }), _jsx("span", { className: "cx-checkbox__label", children: label }), description && _jsx("span", { className: "cx-checkbox__description", children: description })] }));
}
export function Switch({ label, checked, onChange, }) {
    return (_jsxs("button", { type: "button", role: "switch", "aria-checked": checked, className: "cx-switch", onClick: () => onChange(!checked), children: [_jsx("span", { className: "cx-switch__track", "data-control-boundary": "", children: _jsx("span", { className: "cx-switch__thumb" }) }), _jsx("span", { className: "cx-switch__label", children: label })] }));
}
//# sourceMappingURL=primitives.js.map